import dayjs, { extend } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

extend(relativeTime);

export const formatDuration = (seconds: number | null | undefined): string => {
  // Handle null, undefined, or 0
  if (seconds == null || seconds === 0) return "0s";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

/**
 * Concise duration formatter for UI surfaces like toasts where extra zero
 * components feel verbose. Drops trailing zero units:
 *   45      → "45s"
 *   60      → "1m"
 *   125     → "2m 5s"
 *   600     → "10m"
 *   3600    → "1h"
 *   3720    → "1h 2m"
 */
export const formatDurationShort = (seconds: number | null | undefined): string => {
  if (seconds == null || seconds <= 0) return "0s";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s === 0 ? `${m}m` : `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm === 0 ? `${h}h` : `${h}h ${mm}m`;
};

/**
 * Live call-timer format for a ticking clock: m:ss under an hour, h:mm:ss over
 * (so a 2h16m call reads 2:16:19, not 136:19). Keeps seconds visible.
 */
export const formatCallTimer = (seconds: number | null | undefined): string => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const secs = total % 60;
  const mins = Math.floor(total / 60) % 60;
  const hrs = Math.floor(total / 3600);
  const p = (n: number) => n.toString().padStart(2, "0");
  return hrs > 0 ? `${hrs}:${p(mins)}:${p(secs)}` : `${mins}:${p(secs)}`;
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove any non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Handle different lengths of US phone numbers
  if (cleaned.length === 0) {
    return "";
  } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
    // Handle US numbers with country code (1XXXXXXXXXX)
    const withoutCountryCode = cleaned.slice(1);
    return `(${withoutCountryCode.slice(0, 3)}) ${withoutCountryCode.slice(3, 6)}-${withoutCountryCode.slice(6)}`;
  } else if (cleaned.length <= 3) {
    return `(${cleaned}`;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else {
    // If more than 10 digits, take the last 10 digits (in case of country code)
    const lastTen = cleaned.slice(-10);
    return `(${lastTen.slice(0, 3)}) ${lastTen.slice(3, 6)}-${lastTen.slice(6)}`;
  }
};

/**
 * Extract the raw 10-digit phone number from formatted string
 * Used for storing in database (without formatting)
 * @deprecated Use toE164Format from phoneUtils.ts for E.164 storage format
 */
export const extractPhoneNumber = (formattedPhone: string): string => {
  const cleaned = formattedPhone.replace(/\D/g, "");
  
  // If it's 11 digits and starts with 1, remove the country code
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return cleaned.slice(1);
  }
  
  // Return only the last 10 digits (in case country code was included)
  return cleaned.slice(-10);
};

/**
 * Convert phone number to E.164 format for backend storage
 * Recommended for new code - matches backend sanitizePhoneNumber
 */
export const toE164Format = (phoneNumber: string): string => {
  if (!phoneNumber) {
    throw new Error('Phone number is required');
  }

  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // US-focused phone number handling (matches backend logic)
  if (cleaned.length === 10) {
    // Standard US 10-digit number - add US country code
    return '+1' + cleaned;
  }
  
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // US number with country code already included
    return '+' + cleaned;
  }

  // For numbers that don't fit US patterns, try to make them US
  if (cleaned.length >= 10 && cleaned.length <= 11) {
    // Take last 10 digits for US format
    return '+1' + cleaned.slice(-10);
  }

  // For any other format, throw an error since this is US-focused
  throw new Error(
    `Invalid US phone number format: ${phoneNumber}. Expected 10-digit US number.`,
  );
};

/**
 * Enhanced US phone number validation (matches backend logic)
 * Validates area code starts with 2-9 (not 0 or 1)
 */
export const isValidUSPhoneNumber = (phoneNumber: string): boolean => {
  try {
    const e164 = toE164Format(phoneNumber);
    // Must be US format and have valid area code (2-9 for first digit)
    return e164.startsWith('+1') && /^\+1[2-9]\d{9}$/.test(e164);
  } catch (error) {
    return false;
  }
};

/**
 * Get area code from US phone number
 */
export const getUSAreaCode = (phoneNumber: string): string | null => {
  try {
    const e164 = toE164Format(phoneNumber);
    if (e164.startsWith('+1') && e164.length === 12) {
      return e164.substring(2, 5); // Extract 3 digits after +1
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Compare two phone numbers after normalization
 */
export const arePhoneNumbersEqual = (phone1: string, phone2: string): boolean => {
  try {
    const e164_1 = toE164Format(phone1);
    const e164_2 = toE164Format(phone2);
    return e164_1 === e164_2;
  } catch (error) {
    return false;
  }
};

/**
 * Phone validation regex for US numbers (10 digits)
 */
export const US_PHONE_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;

/**
 * Raw phone validation regex (10 digits only)
 */
export const RAW_PHONE_REGEX = /^\d{10}$/;

/**
 * Format currency for display
 * @param amount - Amount to format
 * @param currency - Currency code (default: USD)
 * @param options - Formatting options
 */
export const formatCurrency = (
  amount: number,
  currency: string = "USD",
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    showCents?: boolean;
  } = {}
): string => {
  const {
    minimumFractionDigits,
    maximumFractionDigits,
    showCents = false
  } = options;

  // Smart defaults: show cents only if amount has decimals, unless explicitly requested
  const hasDecimals = amount % 1 !== 0;
  const defaultMinDigits = showCents || hasDecimals ? 2 : 0;
  const defaultMaxDigits = showCents || hasDecimals ? 2 : 0;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: minimumFractionDigits ?? defaultMinDigits,
    maximumFractionDigits: maximumFractionDigits ?? defaultMaxDigits,
  }).format(amount);
};

export const formatRelativeTime = (date: Date | string | null | undefined): string => {
  if (!date) return "Unknown time";

  // Convert to Date object if string
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if valid date
  if (isNaN(dateObj.getTime())) return "Unknown time";

  const now = new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return dateObj.toLocaleDateString();
};

export const formatTime = (time: string) => {
  const [hours, minutes] = time.split(":");
  const date = new Date();
  date.setHours(parseInt(hours), parseInt(minutes));
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export function formatFriendlyDate(dateString: string | Date) {
  const date = dayjs(dateString);
  const now = dayjs();

  const diffDays = now.diff(date, "day");

  if (diffDays < 30) {
    // Less than 30 days: "x days ago"
    return date.fromNow();
  } else if (diffDays < 365) {
    // Less than 1 year: "Jan 15"
    return date.format("MMM D");
  } else {
    // 1 year or more: "Jan 15, 2022"
    return date.format("MMM D, YYYY");
  }
}
